import { Directive, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserStorage } from '@scisys-uim/uim-common';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseTextSectionComponent, TextSectionAction, TextSectionTextTransformer } from './base-text-section.component';

export class SaveTextSectionAction extends TextSectionAction {
  constructor(private component: BasePersistentTextSectionComponent<any>) {
    super();
  }
  title?: string = null;
  icon?: string = "save";
  get available(): boolean {
    return !this.component.readOnly;
  }
  get enabled(): boolean {
    return this.component.unsavedChanges();
  }
  doAction?(): void {
    this.component.save();
    this.component.textSectionComponent.readOnly = true;
    this.component.textSectionComponent.hoverOver(false);
  }
}
export class UndoTextSectionAction extends TextSectionAction {
  constructor(private component: BasePersistentTextSectionComponent<any>) {
    super();
  }
  title?: string = null;
  icon?: string = "undo";
  get available(): boolean {
    return !this.component.readOnly;
  }
  get enabled(): boolean {
    return this.component.unsavedChanges();
  }
  doAction?(): void {
    this.component.undo();
  }

}

@Directive()
export abstract class BasePersistentTextSectionComponent<T> {

  @Input()
  section: string;

  protected textSection: T;
  protected textClone: string;
  private _textTransformer: TextSectionTextTransformer;


  actions: TextSectionAction[];

  @ViewChild('textSectionComponent') textSectionComponent: BaseTextSectionComponent;

  @Output()
  textSectionChange: EventEmitter<T> = new EventEmitter<T>();

  get loaded(): boolean {
    return this.isLoaded();
  }

  private _afterViewInit: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  get afterViewInit(): BehaviorSubject<any> {
    return this._afterViewInit;
  }

  get textTransformer(): TextSectionTextTransformer {
    if (this.textSectionComponent != null) {
      return this.textSectionComponent.textTransformer;
    }
    return this._textTransformer;
  }

  @Input()
  set textTransformer(v: TextSectionTextTransformer) {
    this._textTransformer = v;
    if (this.textSectionComponent != null) {
      this.textSectionComponent.textTransformer = v;
    }
  }

  get text(): string {
    if (this.loaded) {
      return this.getText();
    }
    return "";
  }
  set text(v: string) {
    if (!this.loaded) {
      return;
    }
    this.setText(v);
  }
  get textTransformed(): string {
    if (this.textSectionComponent == null) {
      return this.textSectionComponent.textTransformed;
    }
    return "";
  }

  get editToggleAvailable(): boolean {
    return !this.unsavedChanges();
  }
  get editAvailable(): boolean {
    return this.isEditAvailable();
  }
  get readOnly(): boolean {
    return !this.textSectionComponent || this.textSectionComponent.readOnly;
  }

  constructor(protected userStorage: UserStorage,
    protected snackbar: MatSnackBar) {
    this.actions = [
      new SaveTextSectionAction(this),
      new UndoTextSectionAction(this)
    ];
  }

  ngOnInit() {
    this.userStorage.nextNonNullItem().subscribe(authorisedUser => {
      this.performLoad().subscribe(textSection => this.textSectionChanged(textSection));
    });
  }
  ngAfterViewInit() {
    this.afterViewInit.next(null);
    if (this.textSectionComponent) {
      this.textSectionComponent.afterViewInit.subscribe(event => this.afterTextSectionViewInit());
    }
  }
  private afterTextSectionViewInit() {
    this.afterViewInit.next(null);
    this.textSectionComponent.textTransformer = this.textTransformer;
  }

  undo() {
    this.text = this.textClone;
  }

  protected abstract performSave(): Observable<T>;
  protected abstract performLoad(): Observable<T>;

  save() {
    this.performSave().subscribe(textSection => this.onSaveSuccess(textSection));
  }
  onSaveSuccess(textSection: T): void {
    this.textSectionChanged(textSection);
    this.textSectionChange.emit(textSection);
    this.openSnackbar('Save Successful');
  }

  textSectionChanged(textSection: T): void {
    this.textSection = textSection;
    this.textClone = this.textSection == null ? null : this.text;
  }

  unsavedChanges(): boolean {
    const unsaved: boolean = this.textClone != this.text;
    return unsaved;
  }

  openSnackbar(msg: string, dismissMsg: string = "Dismiss") {
    if (!this.snackbar) {
      return;
    }
    this.snackbar.open(msg, dismissMsg, {
      duration: 3000,
      verticalPosition: 'top'
    });
  }

  abstract isLoaded(): boolean;
  protected abstract getText(): string;
  protected abstract setText(text: string): void;
  protected abstract isEditAvailable(): boolean;
}
